import { useQuery } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useDebounce } from '@/hooks/useDebounce';

export interface PropertyFilters {
  searchTerm?: string;
  organizationIds?: string[];
  availability?: string;
  page?: number;
  pageSize?: number;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
}

export interface PropertyResult {
  id: string;
  name: string | null;
  short_name: string | null;
  title: string;
  short_description: string;
  long_description: string;
  price: number;
  cleaning_fee: number;
  rating: number;
  guests: number;
  bedrooms: number;
  bathrooms: number;
  area: number | null;
  available: boolean;
  featured: boolean;
  images: string[];
  location: string;
  latitude: number | null;
  longitude: number | null;
  host_id: string | null;
  neighborhood_id: string | null;
  airbnb_url: string | null;
  airbnb_ical: string | null;
  property_floor: string | null;
  wifi_network: string | null;
  wifi_password: string | null;
  entry_description: string | null;
  entry_video_url: string | null;
  security_code: string | null;
  operation_started_at: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
  neighborhood_city: string | null;
  neighborhood_state: string | null;
  neighborhood_country: string | null;
  host_name: string | null;
  host_avatar: string | null;
  organization_id: string | null;
  organization_name: string | null;
  total_count: number;
}

export const usePropertiesFiltered = () => {
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>([]);
  const [availabilityFilter, setAvailabilityFilter] = useState<string>('all');

  // Debounce search to avoid excessive backend calls
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Construct backend filters
  const backendFilters: PropertyFilters = useMemo(() => ({
    searchTerm: debouncedSearchTerm || undefined,
    organizationIds: selectedOrganizations.length > 0 ? selectedOrganizations : undefined,
    availability: availabilityFilter !== 'all' ? availabilityFilter : undefined,
    page: currentPage,
    pageSize,
    orderBy: 'sort_order',
    orderDirection: 'asc',
  }), [
    debouncedSearchTerm,
    selectedOrganizations,
    availabilityFilter,
    currentPage,
    pageSize,
  ]);

  // Fetch properties using RPC
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['properties-filtered', backendFilters],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('search_properties', {
        p_search_term: backendFilters.searchTerm || null,
        p_organization_ids: backendFilters.organizationIds || null,
        p_availability: backendFilters.availability || null,
        p_page: backendFilters.page || 1,
        p_page_size: backendFilters.pageSize || 100,
        p_order_by: backendFilters.orderBy || 'sort_order',
        p_order_direction: backendFilters.orderDirection || 'asc',
      });

      if (error) throw error;
      return (data as unknown) as PropertyResult[];
    },
  });

  const properties = data || [];
  const totalCount = properties.length > 0 ? properties[0].total_count : 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  const hasActiveFilters = Boolean(
    searchTerm ||
    selectedOrganizations.length > 0 ||
    availabilityFilter !== 'all'
  );

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedOrganizations([]);
    setAvailabilityFilter('all');
    setCurrentPage(1);
  };

  // Filter state change handlers that reset page
  const handleSearchTermChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleOrganizationsChange = (value: string[]) => {
    setSelectedOrganizations(value);
    setCurrentPage(1);
  };

  const handleAvailabilityChange = (value: string) => {
    setAvailabilityFilter(value);
    setCurrentPage(1);
  };

  return {
    // Data
    properties,
    totalCount,
    totalPages,
    isLoading,
    error,
    refetch,

    // Pagination
    currentPage,
    pageSize,
    handlePageChange,
    handlePageSizeChange,

    // Filters
    searchTerm,
    selectedOrganizations,
    availabilityFilter,
    hasActiveFilters,

    // Filter handlers
    handleSearchTermChange,
    handleOrganizationsChange,
    handleAvailabilityChange,
    resetFilters,
  };
};
